import { LocalDate } from "@js-joda/core";
import ons from "onsenui";
import React from "react";
import * as Ons from "react-onsenui";
import { API } from "./API";
import { AppButton } from "./components/Generic/AppButton";
import { AppTextButton } from "./components/Generic/AppTextButton";
import { ScreenSkeleton } from "./components/ScreenSkeleton";
import { simpleAlert } from "./components/SimpleAlert";
import { callNumber, openExternalUrl } from "./external_link";
import { OrientationLanguages, TranslationMapping } from "./messages";
import {
  CoronaReportAllQuestionsResponse,
  CoronaReportAnswerLabelJsonb,
  CoronaReportQuestionResponseLabelJsonb,
  CoronaReportSubmitParams,
} from "./messages/Corona";
import {
  AgencyId,
  AgencyMemberId,
  CoronaReportAnswerId,
  CoronaReportQuestionId,
  VisitInstanceId,
} from "./messages/ObjectIds";
import { VisitInstanceDetails } from "./messages/Visit";
import { STRINGS } from "./strings";
import { Agency, AgencyWithChatOnlyDetails } from "./system/AppState/AppAgenciesState";
import { AppScheduledVisitsState } from "./system/AppState/AppScheduledVisitsState";
import { globalize, translate } from "./translate";
import { List } from "./utils/List";

interface CovidScreeningTarget {
  visitInstanceId: VisitInstanceId | null;
  agencyId: AgencyId | null;
  patientName: string | null;
}

interface MedflytCovidScreenProps {
  close: () => void;
  onSubmit: (
    answers: CoronaReportSubmitParams,
    visitInstanceId: VisitInstanceId | null
  ) => Promise<void>;
  coronaReports: CoronaReportAllQuestionsResponse;
  agencies: AgencyWithChatOnlyDetails[];
  visitInstances: AppScheduledVisitsState;
  language: OrientationLanguages;
  api: API;
  onAgencyMemberChatClick: (agencyMemberId: AgencyMemberId) => void;
}

interface MedflytCovidScreenState {
  questions: CoronaReportQuestionResponseLabelJsonb[];
  result: CoronaReportSubmitParams;
  visitInstanceId: VisitInstanceId | null;
  isLoading: boolean;
  agencyMemberChatPerson: AgencyMemberId | null;
}

export class MedflytCovidScreen extends React.Component<
  MedflytCovidScreenProps,
  MedflytCovidScreenState
> {
  constructor(props: MedflytCovidScreenProps) {
    super(props);

    this.state = {
      visitInstanceId: null,
      result: { answers: [], patientAgencyId: null, patientName: null },
      isLoading: false,
      questions: [],
      agencyMemberChatPerson: null,
    };
  }

  callCaregiverAgency = () => {
    const randomAgency = this.props.agencies[0];

    if (this.props.agencies.length > 0) {
      if (randomAgency.agency.officePhone !== null) {
        callNumber(randomAgency.agency.officePhone);
      }
    }
  };

  componentDidMount() {
    ons.disableDeviceBackButtonHandler();
    document.addEventListener("backbutton", this.cancel);
  }

  componentWillUnmount() {
    ons.enableDeviceBackButtonHandler();
    document.removeEventListener("backbutton", this.cancel);
  }

  cancel = () => {
    this.props.close();
  };

  checkRisk = (): boolean => {
    let result = false;

    const activeQuestions = this.getActiveQuestions();

    for (let i = 0; i < activeQuestions.length && !result; i++) {
      const currQuestion = activeQuestions[i];
      const answer = this.getAnswerForQuestion(currQuestion.id);

      if (answer === null || typeof answer === "string") {
        continue;
      }
      const filteredAnswers: CoronaReportAnswerLabelJsonb[] = currQuestion.answers.filter(
        (currAnswer) => currAnswer.id === answer
      );
      if (filteredAnswers.length === 0) {
        continue;
      } else {
        result = filteredAnswers[0].inRisk;
      }
    }

    return result;
  };

  getActiveQuestions = (): CoronaReportQuestionResponseLabelJsonb[] => {
    return this.state.questions.filter((curr) => {
      return (
        curr.parentId === null ||
        this.getAnswerForQuestion(curr.parentId) === curr.showIfParentAnswerId
      );
    });
  };

  validate = (): boolean => {
    const activeQuestions = this.getActiveQuestions();
    let result = true;

    for (let i = 0; i < activeQuestions.length && result; i++) {
      result = this.getAnswerForQuestion(activeQuestions[i].id) !== null;
    }

    return result;
  };

  async chatWithAgency(agencyId: AgencyId) {
    const agencyMemberChatPerson = await this.props.api.getCoronaReportChatPerson(agencyId);
    if (agencyMemberChatPerson.agencyMemberId !== null) {
      this.setState({ agencyMemberChatPerson: agencyMemberChatPerson.agencyMemberId });
      simpleAlert({
        message: translate(STRINGS.PLEASE_CHAT_WITH_THE_AGENCY),
        callback: this.navigateToAgencyMemberChatPerson,
        buttonLabel: translate(STRINGS.CHAT_WITH_US),
        icon: "error",
      });
    } else {
      simpleAlert({
        message: translate(STRINGS.PLEASE_CONTACT_YOUR_AGENCY_IMMEDIATELY),
        callback: this.callCaregiverAgency,
        buttonLabel: translate(STRINGS.CALL_NOW),
        icon: "error",
      });
    }
  }

  submit() {
    if (this.validate()) {
      this.setState({ isLoading: true });
      this.props
        .onSubmit(this.state.result, this.state.visitInstanceId)
        .then(() => {
          this.setState({ isLoading: false });
          if (this.checkRisk()) {
            const agencies = this.props.agencies.filter((agency) => !agency.agency.isChatOnly);
            if (agencies.length > 1) {
              simpleAlert({
                message: translate(STRINGS.PLEASE_CONTACT_YOUR_AGENCIES_IMMEDIATELY),
                icon: "error",
                callback: this.props.close,
              });
            } else {
              this.props.close();
              const agencyId = agencies[0].id;
              this.chatWithAgency(agencyId);
            }
          } else {
            simpleAlert({
              message: translate(STRINGS.SUCCESS),
              icon: "success",
              callback: this.props.close,
            });
          }
        })
        .catch((e) => {
          this.setState({ isLoading: false });
          if (e.message === "There is a problem with your Internet connection") {
            simpleAlert({
              message: e.message,
              icon: "error",
              callback: this.props.close,
            });
          } else {
            console.log(e);
            simpleAlert({
              message: translate(STRINGS.SOMETHING_WENT_WRONG),
              icon: "error",
              callback: this.props.close,
            });
          }
        });
    } else {
      simpleAlert({
        message: "Please answer all the questions",
        icon: "error",
      });
    }
  }

  navigateToAgencyMemberChatPerson = () => {
    if (this.state.agencyMemberChatPerson !== null) {
      this.props.onAgencyMemberChatClick(this.state.agencyMemberChatPerson);
    }
  };

  getAnswerForQuestion(questionId: CoronaReportQuestionId): CoronaReportAnswerId | string | null {
    const filterResult = this.state.result.answers.filter((curr) => curr.questionId === questionId);
    const foundAnswer = filterResult[0];

    if (filterResult.length > 0) {
      if (foundAnswer.answerId !== null) {
        return foundAnswer.answerId;
      } else if (foundAnswer.answerFreeText !== null) {
        return foundAnswer.questionId;
      } else {
        return null;
      }
    }
    return null;
  }

  setRadioAnswerForQuestion(
    questionId: CoronaReportQuestionId,
    answerId: CoronaReportAnswerId | null
  ) {
    const nestedQuestions = this.getNestedQuestions(questionId);

    this.setState((state: MedflytCovidScreenState) => {
      const foundAnswer = this.state.result.answers.filter(
        (curr) => curr.questionId === questionId
      )[0];
      const foundAnswerIndex = this.state.result.answers.indexOf(foundAnswer);

      if (foundAnswerIndex !== -1) {
        if (state.result.answers[foundAnswerIndex].answerId === answerId) {
          state.result.answers.splice(foundAnswerIndex, 1);
        } else {
          state.result.answers[foundAnswerIndex].answerId = answerId;
          state.result.answers[foundAnswerIndex].answerFreeText = null;
        }
      } else {
        state.result.answers.push({
          questionId: questionId,
          answerId: answerId,
          answerFreeText: null,
        });
      }

      nestedQuestions.forEach((currNested) => {
        const nestedFoundAnswer = this.state.result.answers.filter(
          (curr) => curr.questionId === currNested.id
        )[0];
        const nestedFoundAnswerIndex = this.state.result.answers.indexOf(nestedFoundAnswer);

        if (nestedFoundAnswerIndex !== -1) {
          state.result.answers.splice(nestedFoundAnswerIndex, 1);
        }
      });

      return state;
    });
  }

  setFreeTextQuestion = (
    questionId: CoronaReportQuestionId,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const questionText = event.target.value.trim();
    if (questionText.length === 0) {
      return;
    }

    const foundAnswer = this.state.result.answers.filter(
      (curr) => curr.questionId === questionId
    )[0];
    const foundAnswerIndex = this.state.result.answers.indexOf(foundAnswer);

    this.setState((prev: MedflytCovidScreenState) => {
      const newState = { ...prev };
      if (foundAnswerIndex !== -1) {
        newState.result.answers[foundAnswerIndex].answerFreeText = questionText;
        newState.result.answers[foundAnswerIndex].answerId = null;
      } else {
        newState.result.answers.push({
          questionId: questionId,
          answerId: null,
          answerFreeText: questionText,
        });
      }
      return newState;
    });
  };

  getNestedQuestions = (questionId: CoronaReportQuestionId) =>
    this.state.questions.filter((curr) => curr.parentId === questionId);

  getTranslatedLabel = (
    label: TranslationMapping<string>,
    language: OrientationLanguages
  ): string => {
    const defaultLang: OrientationLanguages = "English";

    let labelTranslation = label[language];

    if (labelTranslation === undefined) {
      labelTranslation = label[defaultLang];
    }

    return labelTranslation as string;
  };

  renderSingleQuestion(
    question: CoronaReportQuestionResponseLabelJsonb,
    isNested?: boolean
  ): JSX.Element | null {
    const answer = this.getAnswerForQuestion(question.id);

    const nestedQuestions = this.getNestedQuestions(question.id);

    if (
      isNested === undefined ||
      !isNested ||
      (question.parentId !== null &&
        question.showIfParentAnswerId !== null &&
        this.getAnswerForQuestion(question.parentId) === question.showIfParentAnswerId)
    ) {
      return (
        <div
          className="CovidModal__questionContainer slide-fade-in"
          key={"question-" + question.id}
        >
          <div className="CovidModal__questionLabel">
            {this.getTranslatedLabel(question.label, this.props.language)}
          </div>
          {(() => {
            switch (question.inputType) {
              case "NUMBER":
                return this.renderNumberAnswer(question.id);
              case "RADIO":
                return question.answers.map((curr) =>
                  this.renderSingleRadioAnswer(
                    curr.id,
                    this.getTranslatedLabel(curr.label, this.props.language),
                    answer === curr.id,
                    () => this.setRadioAnswerForQuestion(question.id, curr.id)
                  )
                );
              case "TEXT":
                return this.renderFreeTextAnswer(question.id);
              default:
                return null;
            }
          })()}
          {nestedQuestions.map((curr) => this.renderSingleQuestion(curr, true))}
        </div>
      );
    }

    return null;
  }

  renderSingleRadioAnswer(
    id: CoronaReportAnswerId,
    label: string,
    checked: boolean,
    onClick: () => void
  ) {
    return (
      <div
        className="CovidModal__questionAnswerRadioContainer"
        onClick={() => onClick()}
        key={"answer-" + id}
      >
        <div className="checkbox">
          <input
            type={"checkbox"}
            className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
            onChange={() => {
              /* do nothing */
            }}
            checked={checked}
          />
          <span className="checkbox__checkmark" />
        </div>
        <div className="CovidModal__questionAnswerRadioLabel">{label}</div>
      </div>
    );
  }

  renderFreeTextAnswer(questionId: CoronaReportQuestionId) {
    return (
      <div className="CovidModal__questionAnswerRadioContainer" key={"answer-"}>
        <div className="">
          <input
            style={{ color: "#1f1f21", fontWeight: 300 }}
            className={
              "ConfirmPersonalDetailsScreen__input ConfirmPersonalDetailsScreen__freeTextinput"
            }
            type={"text"}
            onChange={(value) => this.setFreeTextQuestion(questionId, value)}
            placeholder={translate(STRINGS.ENTER_TEXT_HERE)}
          />
        </div>
      </div>
    );
  }

  renderNumberAnswer(questionId: CoronaReportQuestionId) {
    return (
      <div className="CovidModal__questionAnswerRadioContainer" key={"answer-"}>
        <div className="">
          <input
            pattern="\d*"
            style={{ color: "#1f1f21", fontWeight: 300 }}
            className={
              "ConfirmPersonalDetailsScreen__input ConfirmPersonalDetailsScreen__freeTextinput"
            }
            type={"number"}
            onChange={(value) => this.setFreeTextQuestion(questionId, value)}
            inputMode="decimal"
            placeholder={translate(STRINGS.ENTER_NUMBER_HERE)}
          />
        </div>
      </div>
    );
  }

  handleBackClick() {
    if (this.props.coronaReports.shouldPopup) {
      simpleAlert({
        message: translate(STRINGS.COVID_REQUIRED_EXPLAINED),
        icon: "error",
      });

      return;
    }

    this.props.close();
  }

  onTargetSelect = (result: CovidScreeningTarget) => {
    const isCaregiver =
      result.visitInstanceId === null && result.patientName === null && result.agencyId === null;
    const questions = isCaregiver
      ? this.props.coronaReports.caregiverQuestions
      : this.props.coronaReports.patientQuestions;

    this.setState({
      visitInstanceId: result.visitInstanceId,
      questions: questions as CoronaReportQuestionResponseLabelJsonb[],
      result: { answers: [], patientAgencyId: result.agencyId, patientName: result.patientName },
    });
  };

  render() {
    const title =
      this.state.questions.length !== 0
        ? (this.state.result.patientAgencyId === null &&
          this.state.result.patientName === null &&
          this.state.visitInstanceId === null
            ? translate(STRINGS.CAREGIVER)
            : translate(STRINGS.PATIENT)) +
          " " +
          translate(STRINGS.SCREENING)
        : translate(STRINGS.COVID) + " " + translate(STRINGS.SCREENING);
    return (
      <ScreenSkeleton
        rootClassName="CovidModal__screenSkeleton"
        title={title}
        titleBarStyle={ScreenSkeleton.TitleBarStyle.PRIMARY}
        onBackClick={() => this.handleBackClick()}
      >
        {this.state.questions.length === 0 ? (
          <CovidModalTargetSelect
            visitInstances={this.props.visitInstances.scheduledVisitDetails.filter((curr) =>
              curr.startTimeLocal.toLocalDate().equals(LocalDate.now())
            )}
            agencies={this.props.agencies}
            onTargetSelected={this.onTargetSelect}
            isCaregiverEnabled={this.props.coronaReports.caregiverQuestions !== null}
            isPatientEnabled={this.props.coronaReports.patientQuestions !== null}
          />
        ) : (
          <div className="CovidModal__container">
            {this.state.result.patientName !== null ? (
              <h2 className="CovidModal__patientTitle slide-fade-in">
                {translate(STRINGS.SCREENING_FOR)}
                <span>{this.state.result.patientName}</span>
              </h2>
            ) : null}
            {this.state.questions
              .sort((a, b) => a.order - b.order)
              .filter((curr) => curr.parentId === null)
              .map((curr) => {
                return this.renderSingleQuestion(curr);
              })}
            {this.state.isLoading ? (
              <div className="VisitsScreen__loadingCont">
                <Ons.ProgressCircular indeterminate={true} />
              </div>
            ) : (
              <>
                <span style={{ flexGrow: 1 }} />
                <AppButton variant="filled" onClick={() => this.submit()}>
                  {translate(STRINGS.SEND_REPORT)}
                </AppButton>
              </>
            )}
          </div>
        )}
      </ScreenSkeleton>
    );
  }
}

interface CovidModalTargetSelectProps {
  onTargetSelected: (result: CovidScreeningTarget) => void;
  agencies: Agency[];
  visitInstances: List<VisitInstanceDetails>;
  isPatientEnabled: boolean;
  isCaregiverEnabled: boolean;
}

interface CovidModalTargetSelectState {
  visitInstanceId: VisitInstanceId | null;
  agencyId: AgencyId | null;
  patientName: string | null;
  isPatientSelected: boolean;
  isSelectingPatientByVisit: boolean;
}

export class CovidModalTargetSelect extends React.Component<
  CovidModalTargetSelectProps,
  CovidModalTargetSelectState
> {
  constructor(props: CovidModalTargetSelectProps) {
    super(props);

    this.state = {
      agencyId: null,
      visitInstanceId: null,
      patientName: null,
      isPatientSelected: false,
      isSelectingPatientByVisit: true,
    };
  }

  selectCaregiver() {
    this.props.onTargetSelected({ agencyId: null, visitInstanceId: null, patientName: null });
  }

  validate = (): boolean => {
    let result = true;

    // Validation is done only for patient, caregiver is null in every field
    if (this.state.isPatientSelected) {
      if (this.state.isSelectingPatientByVisit) {
        if (this.state.visitInstanceId === null) {
          simpleAlert({
            message: translate(STRINGS.NO_SCHEDULED_VISITS_TODAY),
            icon: "error",
          });

          result = false;
        }
      } else {
        if (this.state.agencyId === null) {
          simpleAlert({
            message: translate(STRINGS.THERE_ARE_NO_AGENCIES_YET),
            icon: "error",
          });

          result = false;
        } else if (this.state.patientName === null || this.state.patientName.length === 0) {
          simpleAlert({
            message: translate(STRINGS.PLEASE_ENTER_PATIENT_NAME),
            icon: "error",
          });

          result = false;
        } else if (
          this.state.patientName.length < 6 ||
          this.state.patientName.indexOf(" ") === -1
        ) {
          simpleAlert({
            message: translate(STRINGS.PLEASE_ENTER_PATIENT_FIRST_AND_LAST_NAME),
            icon: "error",
          });

          result = false;
        }
      }
    }

    return result;
  };

  submit() {
    if (this.validate()) {
      this.props.onTargetSelected({
        agencyId: this.state.agencyId,
        patientName: this.state.patientName,
        visitInstanceId: this.state.visitInstanceId,
      });
    }
  }

  selectPatient = () => {
    if (this.props.visitInstances.length() > 0) {
      this.selectPatientVisitOption();
    } else if (this.props.agencies.length > 0) {
      this.selectPatientAgencyOption();
    }
  };

  selectPatientAgencyOption = () => {
    let firstAgencyId: AgencyId | null = null;

    if (this.props.agencies.length > 0) {
      firstAgencyId = this.props.agencies[0].id;
    }

    this.setState({
      agencyId: firstAgencyId,
      patientName: "",
      visitInstanceId: null,
      isSelectingPatientByVisit: false,
      isPatientSelected: true,
    });
  };

  selectPatientVisitOption = () => {
    const firstVisit = this.props.visitInstances.first();
    let firstVisitId: VisitInstanceId | null = null;
    let firstVisitPatientName: string | null = null;

    if (firstVisit !== undefined) {
      firstVisitId = firstVisit.id;
      firstVisitPatientName = firstVisit.confidentialVisitDetails.patientName;
    }

    this.setState({
      visitInstanceId: firstVisitId,
      patientName: firstVisitPatientName,
      agencyId: null,
      isSelectingPatientByVisit: true,
      isPatientSelected: true,
    });
  };

  render() {
    return (
      <div className="CovidTargetSelect__container slide-fade-in">
        {!this.state.isPatientSelected ? (
          <>
            <h2>{translate(STRINGS.SCREENING_FOR)}</h2>
            {this.props.isCaregiverEnabled ? (
              <AppButton onClick={() => this.selectCaregiver()} variant="filled">
                {translate(STRINGS.YOURSELF)}
              </AppButton>
            ) : null}
            {this.props.isPatientEnabled ? (
              <AppButton onClick={() => this.selectPatient()} variant="filled">
                {translate(STRINGS.PATIENT)}
              </AppButton>
            ) : null}
          </>
        ) : (
          <>
            <div className="CovidTargetSelect__patientOptionWrapper">
              <div
                className="CovidTargetSelect__patientOptionTitleContainer"
                onClick={() => this.selectPatientVisitOption()}
              >
                <div className="checkbox">
                  <input
                    type={"checkbox"}
                    className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
                    onChange={() => {
                      /* do nothing */
                    }}
                    checked={this.state.isSelectingPatientByVisit}
                  />
                  <span className="checkbox__checkmark" />
                </div>
                <h3>{translate(STRINGS.BY_EXISTING_VISIT)}</h3>
              </div>
              {this.state.isSelectingPatientByVisit ? (
                <div className="CovidModal__formFields slide-fade-in">
                  {this.state.visitInstanceId === null ? (
                    <div>{translate(STRINGS.NO_SCHEDULED_VISITS_TODAY)}</div>
                  ) : (
                    <select
                      className="CovidTargetSelect__agencySelect"
                      onChange={(event) => {
                        this.setState({
                          visitInstanceId: VisitInstanceId.wrap(parseInt(event.target.value, 10)),
                        });
                      }}
                      value={VisitInstanceId.unwrap(this.state.visitInstanceId)}
                    >
                      {this.props.visitInstances.map((curr) => {
                        return (
                          <option
                            key={VisitInstanceId.unwrap(curr.id)}
                            value={VisitInstanceId.unwrap(curr.id)}
                          >
                            {`${curr.confidentialVisitDetails.patientName} - ${translate(
                              STRINGS.STARTING_AT
                            )}: ${globalize().formatLocalDateTime(curr.startTimeLocal, {
                              time: "short",
                            })}`}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              ) : null}
            </div>
            <div className="CovidTargetSelect__patientOptionWrapper">
              <div
                className="CovidTargetSelect__patientOptionTitleContainer"
                onClick={() => this.selectPatientAgencyOption()}
              >
                <div className="checkbox">
                  <input
                    type={"checkbox"}
                    className={"PlanOfCareComponent__basicCheckBox checkbox__input"}
                    onChange={() => {
                      /* do nothing */
                    }}
                    checked={!this.state.isSelectingPatientByVisit}
                  />
                  <span className="checkbox__checkmark" />
                </div>
                <h3>{translate(STRINGS.BY_PATIENT_NAME)}</h3>
              </div>
              {!this.state.isSelectingPatientByVisit ? (
                <div className="CovidModal__formFields slide-fade-in">
                  {this.state.agencyId === null ? (
                    <div>{translate(STRINGS.THERE_ARE_NO_AGENCIES_YET)}</div>
                  ) : (
                    <>
                      <div>
                        <h5>{translate(STRINGS.AGENCY)}:</h5>
                        <select
                          className="CovidTargetSelect__agencySelect"
                          onChange={(event) => {
                            this.setState({
                              agencyId: AgencyId.wrap(parseInt(event.target.value, 10)),
                            });
                          }}
                          value={AgencyId.unwrap(this.state.agencyId)}
                        >
                          {this.props.agencies.map((curr) => {
                            return (
                              <option
                                key={AgencyId.unwrap(curr.id)}
                                value={AgencyId.unwrap(curr.id)}
                              >
                                {curr.agency.name}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <h5>{translate(STRINGS.PATIENT_NAME)}:</h5>
                        <input
                          onChange={(event) => this.setState({ patientName: event.target.value })}
                          className="CovidTargetSelect__patientNameInput"
                        />
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
            <span style={{ flexGrow: 1 }} />
            <div>
              <AppButton onClick={() => this.submit()} style={{ flexGrow: 1 }}>
                {translate(STRINGS.NEXT)}
              </AppButton>
            </div>
          </>
        )}
        <div className="__seeMoreInformation">
          <AppTextButton
            color="black"
            onClick={() =>
              openExternalUrl(
                "https://www1.nyc.gov/assets/doh/downloads/pdf/imm/covid-19-home-community-healthcare-workers.pdf"
              )
            }
          >
            {translate(STRINGS.SEE_MORE_INFORMATION_ABOUT_PRE_SCREENING_REQUIREMENT)}
          </AppTextButton>
        </div>
      </div>
    );
  }
}
